import { type useCart } from "@shopify/hydrogen-react";
import {
  type MoneyInput,
  type Product,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";

import {
  type CartProduct,
  type CartProductVariant,
  type CartSectionSettings,
} from "./cart";

export const CART_UPSELL_VARIANT_SOURCES = {
  BUNDLE_TRANSFORM: "bundle_transform",
  FALLBACK_COLLECTION: "fallback_collection",
  MANUAL_OVERRIDE: "manual_override",
  UPSELLS_API: "upsells_api",
} as const;

export type CartUpsellVariantSource =
  (typeof CART_UPSELL_VARIANT_SOURCES)[keyof typeof CART_UPSELL_VARIANT_SOURCES];

export type CartUpsellProductMetadata = {
  bundleDeltaPrice?: MoneyInput;
  bundleDiscountPercent?: number | null;
  bundleProduct?: CartProduct;
  id: Product["id"];
  product: CartProduct;
  relatedCartLines?: ReturnType<typeof useCart>["lines"];
  selectedOptions: SelectedOption[];
  selectedVariant: CartProductVariant;
  source?: CartUpsellVariantSource;
};

/** Upsell metadata after merge stamping — required for view-event analytics. */
export type CartUpsellProductMetadataWithSource = CartUpsellProductMetadata & {
  source: CartUpsellVariantSource;
};

/** Experiment attribution from the Upsells Data API (not cart-specific). */
export type UpsellsApiExperiment = {
  id: string;
  name: string;
  variant: number;
};

export type CartUpsellNestedProperties = {
  cart: {
    items: Array<{
      price: string;
      "product title": string;
      quantity: number;
      sku: string;
      "variant id": number;
      "variant title": string;
    }>;
    totals: {
      price: string;
      quantity: number;
    };
  };
  customer: {
    email: CartSectionSettings["meta"]["customer"]["email"];
    "logged in": boolean;
    "shopify id": CartSectionSettings["meta"]["customer"]["id"];
    "total orders": CartSectionSettings["meta"]["customer"]["ordersCount"];
    "total spent": CartSectionSettings["meta"]["customer"]["totalSpent"];
  };
  upsells: {
    api_available: boolean;
    experiment?: UpsellsApiExperiment;
    variants: Array<{
      price: string;
      "product title": string;
      sku: string;
      source: CartUpsellVariantSource;
      "variant id": number;
      "variant title": string;
    }>;
  };
};

export type CartUpsellEventProperties = {
  "cart - items - prices": string[];
  "cart - items - product titles": string[];
  "cart - items - quantities": number[];
  "cart - items - skus": string[];
  "cart - items - variant ids": number[];
  "cart - items - variant titles": string[];
  "cart - totals - price": string;
  "cart - totals - quantity": number;
  "cart token": string | undefined;
  "customer - email": CartSectionSettings["meta"]["customer"]["email"];
  "customer - logged in": boolean;
  "customer - shopify id": CartSectionSettings["meta"]["customer"]["id"];
  "customer - total orders": CartSectionSettings["meta"]["customer"]["ordersCount"];
  "customer - total spent": CartSectionSettings["meta"]["customer"]["totalSpent"];
  market: string;
  nested_properties: CartUpsellNestedProperties;
  "upsells - api available": boolean;
  "upsells - experiment id"?: string;
  "upsells - experiment name"?: string;
  "upsells - experiment variant"?: number;
  "upsells - prices": string[];
  "upsells - product titles": string[];
  "upsells - skus": string[];
  "upsells - sources": CartUpsellVariantSource[];
  "upsells - variant ids": number[];
  "upsells - variant titles": string[];
};

export type CartUpsellLine = {
  merchandise: {
    id: string;
    price: { amount: string };
    product: { title: string };
    sku?: string | null;
    title: string;
  };
  quantity?: number | null;
};
